import { useState, useCallback } from 'react';
import { Basemap, BasemapId } from '../lib/types';
import { BASEMAPS, DEFAULT_BASEMAP_ID, isBasemapId } from '../lib/basemaps';

const STORAGE_KEY = 'sfi.basemapId';

const readStoredBasemapId = (): BasemapId | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isBasemapId(raw) ? raw : null;
  } catch {
    return null;
  }
};

const writeStoredBasemapId = (id: BasemapId) => {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // ignore quota / privacy-mode failures — in-memory state still updates
  }
};

const clearStoredBasemapId = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export interface UseBasemapSelection {
  basemap: Basemap;
  basemapId: BasemapId;
  setBasemapId: (id: BasemapId) => void;
  clearChoice: () => void;
}

export const useBasemapSelection = (): UseBasemapSelection => {
  const initialStoredId = readStoredBasemapId();
  // Default to satellite (DEFAULT_BASEMAP_ID) so the dashboard renders
  // immediately on first visit without forcing a basemap-picker gate.
  // Returning visitors still get their stored choice. The picker is now
  // discovered via the corner layers button + a one-time hint, not a
  // mandatory first-screen modal.
  const [basemapId, setBasemapIdState] = useState<BasemapId>(initialStoredId ?? DEFAULT_BASEMAP_ID);

  const setBasemapId = useCallback((id: BasemapId) => {
    setBasemapIdState(id);
    writeStoredBasemapId(id);
  }, []);

  const clearChoice = useCallback(() => {
    setBasemapIdState(DEFAULT_BASEMAP_ID);
    clearStoredBasemapId();
  }, []);

  return {
    basemap: BASEMAPS[basemapId],
    basemapId,
    setBasemapId,
    clearChoice,
  };
};
